// Tenant inactive lifecycle (2026-09-29) — the frontend half of api/Shared/TenantLifecycle.psm1.
//
// A tenant whose collection is off is ALWAYS status 'inactive'; `inactiveReason` tells the states apart:
//   paused            — collection will resume later.
//   retained          — collection off, logs kept (billable retention); still selectable for investigations.
//   pending_deletion  — purged on `purgeDate`. `purgeLogs === 'TRUE'` also requests deletion of the tenant's
//                       LOG data, which the data teams carry out; the portal only deletes its own records.
// Legacy rows: status 'deleted' (the old soft-delete) reads as pending_deletion; an inactive row with no reason
// reads as retained. The API resolves `inactiveReason` already — these helpers only render it.
//
// The date rules MIRROR the server (which is authoritative and re-validates everything):
//   purgeDate >= date(deactivatedUtc) + PURGE_MIN_GAP_DAYS, never in the past; default = deactivation + 30 days.

export const PURGE_MIN_GAP_DAYS = 2;
export const PURGE_DEFAULT_DAYS = 30;

export const INACTIVE_REASONS = [
  {
    value: 'paused',
    label: 'Paused',
    description: 'Collection stops for now and is expected to resume. Logs are kept.',
  },
  {
    value: 'retained',
    label: 'Retained',
    description:
      'Collection stops for good, but the logs are kept for as long as the client pays for retention. The tenant stays available for investigations and exports.',
  },
  {
    value: 'pending_deletion',
    label: 'Pending deletion',
    description: 'The tenant is deleted on a date you choose.',
  },
];

const DAY_MS = 24 * 60 * 60 * 1000;

// 'yyyy-MM-dd' for a UTC date.
export function toIsoDay(date) {
  return date.toISOString().slice(0, 10);
}

// UTC midnight of a 'yyyy-MM-dd' or ISO timestamp, or null.
export function utcDay(value) {
  if (!value) return null;
  const s = String(value);
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00Z` : s);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function addDays(day, n) {
  return new Date(day.getTime() + n * DAY_MS);
}

// Earliest allowed purge date and the default, for a tenant (null deactivatedUtc = being deactivated now).
// `now` is injectable for tests.
export function purgeDateBounds(deactivatedUtc, now = new Date()) {
  const today = utcDay(now.toISOString());
  const anchor = utcDay(deactivatedUtc) || today;
  let min = addDays(anchor, PURGE_MIN_GAP_DAYS);
  if (min < today) min = today;
  let def = addDays(anchor, PURGE_DEFAULT_DAYS);
  if (def < min) def = min;
  return { min: toIsoDay(min), defaultDate: toIsoDay(def) };
}

// Whole days from today (UTC) until a 'yyyy-MM-dd' date; negative when past; null when unparseable.
export function daysUntil(isoDay, now = new Date()) {
  const d = utcDay(isoDay);
  if (!d) return null;
  return Math.round((d - utcDay(now.toISOString())) / DAY_MS);
}

export function isInactiveStatus(status) {
  return status === 'inactive' || status === 'deleted';
}

// The one badge label + tone for a tenant's lifecycle state. Tones match ui/Badge.
export function lifecycleBadge(tenant, now = new Date()) {
  if (!tenant) return null;
  const { status } = tenant;
  if (!isInactiveStatus(status)) return null;
  const reason = tenant.inactiveReason || (status === 'deleted' ? 'pending_deletion' : 'retained');
  if (reason === 'paused') return { label: 'Paused', tone: 'neutral' };
  if (reason === 'retained') return { label: 'Retained', tone: 'info' };
  // pending_deletion — legacy 'deleted' rows carry no purgeDate; their purge is deletedDate + 30.
  let purgeDate = tenant.purgeDate;
  if (!purgeDate && status === 'deleted' && tenant.deletedDate) {
    const d = utcDay(tenant.deletedDate);
    if (d) purgeDate = toIsoDay(addDays(d, PURGE_DEFAULT_DAYS));
  }
  if (tenant.portalPurgedUtc) {
    const lp = logPurgeView(tenant);
    if (lp && lp.teams.some((t) => t.status === 'refused')) return { label: 'Log deletion needs review', tone: 'critical' };
    return { label: 'Awaiting log deletion', tone: 'critical' };
  }
  const n = daysUntil(purgeDate, now);
  if (n === null) return { label: 'Pending deletion', tone: 'critical' };
  if (n <= 0) return { label: 'Deletes at next run', tone: 'critical' };
  return { label: `Deletes in ${n} day${n === 1 ? '' : 's'}`, tone: 'critical' };
}

// Plain-English summary of what a pending deletion will do. Deliberately does not promise log deletion as
// done by the portal: the data teams carry it out. And it never says "all data": cost history and some
// operational records (per-tenant cost and usage figures, monitoring telemetry) are kept by design.
export function describeDeletion(purgeLogs) {
  const logs = purgeLogs === true || String(purgeLogs).toUpperCase() === 'TRUE';
  return logs
    ? 'Deletes the portal records (cases, investigation results, safe IPs) and requests deletion of the tenant’s log data. Log deletion is carried out by the data teams; until each confirms it, the tenant stays listed as “Awaiting log deletion”. Cost history and operational records are kept.'
    : 'Deletes the portal records (cases, investigation results, safe IPs) and removes the tenant from the portal. Its log data is kept.';
}

// ── Log deletion across the data teams (TenantLogPurgeStatus, 2026-09-30) ────────────────────────────────
// Mirrors $script:LogPurgeTeams in TenantLifecycle.psm1 (a test reads the psm1 and compares).
export const LOG_PURGE_TEAMS = [
  { team: 'log-collection', label: 'Log Collection', covers: 'raw collected logs (bronze)' },
  { team: 'silver', label: 'Silver', covers: 'staging and the silver log files' },
  { team: 'pipeline', label: 'Pipeline', covers: 'Delta tables, event mirror, alert evidence' },
  // Deleted by the portal's purge runbook on the Notification-Dispatcher's behalf (it may not delete rows).
  { team: 'dispatcher', label: 'Notification Dispatcher', covers: 'alert delivery log and ticket links' },
];

const LOG_PURGE_STATUS_TEXT = {
  done: { statusLabel: 'Done', tone: 'success' },
  failed: { statusLabel: 'Failed — retrying', tone: 'warning' },
  refused: { statusLabel: 'Refused — needs review', tone: 'critical' },
  unknown: { statusLabel: 'Unrecognised status', tone: 'warning' },
  pending: { statusLabel: 'Not reported yet', tone: 'neutral' },
};

// Per-team rows for display, from the API's `logPurge` object. Returns null when the tenant has no log
// deletion requested. `available: false` (table unreadable) is surfaced as its own state, never as done.
export function logPurgeView(tenant) {
  if (!tenant || tenant.inactiveReason !== 'pending_deletion') return null;
  if (String(tenant.purgeLogs).toUpperCase() !== 'TRUE') return null;
  const lp = tenant.logPurge;
  if (!lp || lp.available === false) {
    return { available: false, complete: false, state: 'unavailable', teams: [] };
  }
  const byTeam = Object.fromEntries((lp.teams || []).map((t) => [t.team, t]));
  const teams = LOG_PURGE_TEAMS.map((t) => {
    const row = byTeam[t.team] || {};
    const status = LOG_PURGE_STATUS_TEXT[row.status] ? row.status : row.status ? 'unknown' : 'pending';
    return {
      ...t,
      status,
      ...LOG_PURGE_STATUS_TEXT[status],
      completedUtc: row.completedUtc || '',
      detail: row.detail || '',
    };
  });
  const complete = teams.every((t) => t.status === 'done');
  return { available: true, complete, state: lp.state || (complete ? 'complete' : 'waiting'), teams };
}

// Purge dates already held by OTHER tenants deleting their log data. Mirrors Get-TenantTakenPurgeDates:
// Silver deletes one tenant per run and refuses a run when two qualify, so two log deletions never share a
// date. The server re-checks against the full tenant list.
export function takenPurgeDates(tenants, exceptTenantId) {
  const except = String(exceptTenantId || '').toLowerCase();
  const dates = new Set();
  for (const t of tenants || []) {
    if (!t || String(t.tenantId || '').toLowerCase() === except) continue;
    if (t.inactiveReason !== 'pending_deletion') continue;
    if (String(t.purgeLogs).toUpperCase() !== 'TRUE') continue;
    if (/^\d{4}-\d{2}-\d{2}$/.test(t.purgeDate || '')) dates.add(t.purgeDate);
  }
  return [...dates].sort();
}

// The first date on or after `isoDay` that is not in `taken`.
export function nextFreePurgeDate(isoDay, taken) {
  const set = new Set(taken || []);
  let d = utcDay(isoDay);
  if (!d) return isoDay;
  while (set.has(toIsoDay(d))) d = addDays(d, 1);
  return toIsoDay(d);
}
